// frontend/src/modules/projects/pages/ProjectsPage.tsx
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Plus, Briefcase, Clock4, FolderKanban, Play, ArrowUpRight } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { useApi } from '../../../design-system/useApi';
import { usePermission } from '../../../core/auth/usePermission';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { formatDate, formatDateTime } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { TimerWidget } from '../components/TimerWidget';
import { CreateProjectDialog } from '../components/CreateProjectDialog';
import { ManualEntryDialog } from '../components/ManualEntryDialog';
import type { Project, Task, TaskStatus, TimeEntry } from '../types';

export function ProjectsPage() {
  // Branch on the user's REAL permissions (from /api/auth/me) instead of the
  // old fetch-probe ("does GET /projects load?"). The probe checked
  // projects.view — which employees also hold — so view-only users landed in
  // the owner view full of write controls that 403'd on click, while the
  // employee view was unreachable for anyone who could actually use it.
  const canWrite = usePermission('projects.write'); // matches api/modules/projects/routes.ts write gates
  const canView = usePermission('projects.view');

  if (canWrite) return <OwnerView />;
  if (canView) return <EmployeeView />;
  return <NoProjectAccess />;
}

/* ─────────────────────────── Owner view ─────────────────────────── */

type ProjectStatusFilter = 'all' | 'active' | 'archived';

function OwnerView() {
  // The backend list defaults to active-only and `?status=` accepts exactly one
  // status (api/modules/projects/routes.ts → listProjects), so "All" is the
  // union of the two fetches. Fetching both also keeps BOTH KPIs truthful
  // regardless of which filter is selected.
  const activeQ   = useApi<{ projects: Project[] }>('/api/projects/projects');
  const archivedQ = useApi<{ projects: Project[] }>('/api/projects/projects?status=archived');
  const [statusFilter, setStatusFilter] = React.useState<ProjectStatusFilter>('active');
  const [createOpen, setCreateOpen] = React.useState(false);
  const [manualOpen, setManualOpen] = React.useState(false);

  const activeProjects = activeQ.data?.projects ?? [];
  const archivedProjects = archivedQ.data?.projects ?? [];
  const projects =
    statusFilter === 'active' ? activeProjects :
    statusFilter === 'archived' ? archivedProjects :
    [...activeProjects, ...archivedProjects];
  const active = activeProjects.length;
  const archived = archivedProjects.length;

  const listLoading = statusFilter === 'active' ? activeQ.loading
    : statusFilter === 'archived' ? archivedQ.loading
    : (activeQ.loading || archivedQ.loading);
  const listError = statusFilter === 'active' ? activeQ.error
    : statusFilter === 'archived' ? archivedQ.error
    : (activeQ.error ?? archivedQ.error);
  const reloadAll = () => { activeQ.reload(); archivedQ.reload(); };

  return (
    <div>
      <PageHeader
        eyebrow="Work / Projects"
        title="Projects"
        description="Track time across projects and tasks. Timer running widget below."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setManualOpen(true)}
              className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
            >
              <Clock4 className="h-3.5 w-3.5" />
              Manual entry
            </Button>
            <Button
              onClick={() => setCreateOpen(true)}
              className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              New project
            </Button>
          </div>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {/* KPI strip + timer row */}
        <section className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_2fr] gap-3 items-stretch">
          <Kpi label="Active" value={active} caption="In progress" icon={<Briefcase className="h-3.5 w-3.5" />} tone="success" />
          <Kpi label="Archived" value={archived} caption="Closed out" icon={<FolderKanban className="h-3.5 w-3.5" />} tone="muted" />
          <div className={[VISUAL.bento, 'p-5 lg:p-6 flex items-center gap-4'].join(' ')}>
            <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 shrink-0">
              <Play className="h-4 w-4 text-saffron-foreground fill-current ml-0.5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={[VISUAL.eyebrow, 'mb-1.5'].join(' ')}>Now tracking</p>
              <TimerWidget />
            </div>
          </div>
        </section>

        {/* Status filter */}
        <section className="flex items-center gap-1.5">
          {(['active', 'archived', 'all'] as ProjectStatusFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setStatusFilter(f)}
              className={[
                'inline-flex items-center h-8 px-3.5 rounded-full text-[12px] font-semibold transition-colors ring-1',
                statusFilter === f
                  ? 'bg-foreground text-background ring-foreground'
                  : 'bg-card/70 text-muted-foreground ring-border/50 hover:text-foreground',
              ].join(' ')}
            >
              {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Archived'}
            </button>
          ))}
        </section>

        {/* Projects list */}
        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          {listLoading ? (
            <RowSkeleton />
          ) : listError ? (
            <ErrorState title="Couldn't load projects" description={listError} onRetry={reloadAll} />
          ) : projects.length === 0 ? (
            statusFilter === 'archived' ? (
              <SoftEmpty title="No archived projects" body="Projects you archive will show up here, ready to restore." />
            ) : (
              <EmptyProjects onCreate={() => setCreateOpen(true)} />
            )
          ) : (
            <ul className="divide-y divide-border/40">
              {projects.map((p, i) => (
                <motion.li
                  key={p.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.025, 0.18) }}
                >
                  <Link
                    to={`/dashboard/projects/${p.id}`}
                    className="flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors"
                  >
                    <div className="h-11 w-11 rounded-2xl bg-muted/50 ring-1 ring-border/40 flex items-center justify-center shrink-0">
                      <Briefcase className="h-4 w-4 text-muted-foreground" strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">{p.name}</span>
                        {p.code && <span className="text-[11.5px] font-mono text-muted-foreground">{p.code}</span>}
                      </div>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        Created {formatDate(p.created_at)}
                        {/* Migration 080: a project with no roster is OPEN to
                            everyone — worth saying, since a rostered project
                            refuses time from anyone not on the list. */}
                        {p.member_count != null && (
                          <> · {p.member_count > 0
                            ? `${p.member_count} assigned`
                            : 'Open to everyone'}</>
                        )}
                      </p>
                    </div>
                    {p.status === 'archived'
                      ? <span className={statusPill('muted')}><span className={statusDot('muted')} />Archived</span>
                      : <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>}
                  </Link>
                </motion.li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(p) => { setCreateOpen(false); reloadAll(); toast.success(`Created ${p.name}`); }}
      />
      <ManualEntryDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        onAdded={() => { setManualOpen(false); }}
      />
    </div>
  );
}

/* ─────────────────────────── Employee view ─────────────────────────── */

function EmployeeView() {
  const [manualOpen, setManualOpen] = React.useState(false);
  // TODO(my-tasks filter): GET /api/projects/tasks supports
  // ?assignee_employee_id=…, but there is no cheap client-side way to resolve
  // the CURRENT user's employee id (no /employees/me endpoint, and
  // /api/hr/employees needs HR permissions employees don't hold). Until a
  // small "my employee id" surface exists (e.g. on /api/auth/me), show all
  // visible tasks unfiltered.
  const tasksQ   = useApi<{ tasks: Task[] }>('/api/projects/tasks');
  const entriesQ = useApi<{ rows: TimeEntry[] }>('/api/projects/time-entries/me');

  return (
    <div>
      <PageHeader
        eyebrow="Work / My work"
        title="Your tasks + time"
        description="Track what you ship and log your hours."
        actions={
          <Button
            variant="outline"
            onClick={() => setManualOpen(true)}
            className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
          >
            <Clock4 className="h-3.5 w-3.5" />
            Manual entry
          </Button>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        <section className={[VISUAL.bento, 'p-5 lg:p-6 flex items-center gap-4'].join(' ')}>
          <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 shrink-0">
            <Play className="h-4 w-4 text-saffron-foreground fill-current ml-0.5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={[VISUAL.eyebrow, 'mb-1.5'].join(' ')}>Now tracking</p>
            <TimerWidget />
          </div>
        </section>

        <section>
          <h3 className={[VISUAL.dashSection, 'mb-3'].join(' ')}>My tasks</h3>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            {tasksQ.loading ? <RowSkeleton /> : tasksQ.error ? (
              <ErrorState title="Couldn't load tasks" description={tasksQ.error} onRetry={tasksQ.reload} />
            ) : (tasksQ.data?.tasks ?? []).length === 0 ? (
              <SoftEmpty title="No tasks" body="Your manager hasn't assigned any tasks yet." />
            ) : (
              <ul className="divide-y divide-border/40">
                {(tasksQ.data?.tasks ?? []).map((t) => (
                  <li key={t.id} className="flex items-center gap-4 px-5 py-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-semibold tracking-[-0.005em] text-foreground truncate">{t.title}</p>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        {t.due_date ? `Due ${formatDate(t.due_date)}` : 'No due date'}
                      </p>
                    </div>
                    {taskStatusPill(t.status)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section>
          <h3 className={[VISUAL.dashSection, 'mb-3'].join(' ')}>Recent time entries</h3>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            {entriesQ.loading ? <RowSkeleton /> : entriesQ.error ? (
              <ErrorState title="Couldn't load time entries" description={entriesQ.error} onRetry={entriesQ.reload} />
            ) : (entriesQ.data?.rows ?? []).length === 0 ? (
              <SoftEmpty title="No time logged yet" body="Start a timer above to log your first entry." />
            ) : (
              <ul className="divide-y divide-border/40">
                {(entriesQ.data?.rows ?? []).map((r) => (
                  <li key={r.id} className="flex items-center gap-4 px-5 py-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-semibold text-foreground">{formatDateTime(r.start_at)}</p>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        {r.end_at ? `Ended ${formatDateTime(r.end_at)}` : 'Running now'} · source: {r.source}
                      </p>
                      {r.notes ? <p className="text-[12px] text-muted-foreground/90 mt-0.5 truncate">{r.notes}</p> : null}
                    </div>
                    {r.end_at ? (
                      <span className={statusPill('muted')}><span className={statusDot('muted')} />Logged</span>
                    ) : (
                      <span className={statusPill('success')}><span className={statusDot('success')} />Running</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <ManualEntryDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        onAdded={() => { setManualOpen(false); entriesQ.reload(); }}
      />
    </div>
  );
}

/* ─────────────────────────── no-access view ─────────────────────────── */

// Rendered when the user holds neither projects.write nor projects.view —
// previously they fell into EmployeeView whose fetches all 403'd.
function NoProjectAccess() {
  return (
    <div>
      <PageHeader
        eyebrow="Work / Projects"
        title="Projects"
        description="Time tracking and project management."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12">
        <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          <SoftEmpty
            title="No project access"
            body="Your role doesn't include project access. Ask a workspace admin to grant it if you need to track time here."
          />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── shared ─────────────────────────── */

function Kpi({
  label, value, caption, icon, tone = 'neutral',
}: { label: string; value: number; caption: string; icon: React.ReactNode; tone?: 'neutral' | 'success' | 'saffron' | 'muted' }) {
  const toneClass =
    tone === 'success' ? 'bg-success/10 text-success ring-success/25' :
    tone === 'saffron' ? 'bg-saffron/15 text-saffron-foreground ring-saffron/30' :
    tone === 'muted'   ? 'bg-muted text-muted-foreground ring-border/50' :
                         'bg-muted text-muted-foreground ring-border/50';
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <div className="flex items-center justify-between mb-3">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span className={['inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1', toneClass].join(' ')}>{icon}</span>
      </div>
      <p className="font-sans font-light text-[2.2rem] leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</p>
      <p className="text-[12px] text-muted-foreground mt-2">{caption}</p>
    </div>
  );
}

function RowSkeleton() {
  return (
    <ul className="divide-y divide-border/40">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="h-11 w-11 rounded-2xl bg-muted/60 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-muted/60 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-muted/40 animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function SoftEmpty({ title, body }: { title: string; body: string }) {
  return (
    <div className="py-10 px-6 text-center">
      <p className="text-[14px] font-semibold text-foreground">{title}</p>
      <p className="text-[13px] text-muted-foreground mt-1">{body}</p>
    </div>
  );
}

function EmptyProjects({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="py-14 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 flex items-center justify-center">
        <Briefcase className="h-6 w-6 text-saffron-foreground" strokeWidth={1.6} />
      </div>
      <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">Start a project</p>
      <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
        Group tasks, track time, and pull reports — all under one roof.
      </p>
      <div className="mt-6">
        <Button onClick={onCreate} className="rounded-full h-10 px-5 font-semibold gap-1.5">
          <Plus className="h-3.5 w-3.5" /> New project <ArrowUpRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function taskStatusPill(s: TaskStatus): React.ReactNode {
  const tone: StatusTone =
    s === 'done'        ? 'success' :
    s === 'in_progress' ? 'info' :
    s === 'cancelled'   ? 'destructive' :
    'muted';
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {s.replace('_', ' ')}
    </span>
  );
}
